import { CanceledError } from 'axios';
import { DateTime } from 'luxon';
import { capitalize } from 'vue';

import GlobalEventBus from '@/GlobalEventBus';
import GlobalEvents from '@/GlobalEvents';
import { HttpStatusCodeNoResponse } from '@/globals';
import UserNotifications from '@/utils/UserNotifications';
import i18n from '@/vueI18n';

interface IError {
  status?: number;
  response?: {
    data?: {
      error?: string;
    };
  };
  userFriendlyMessage?: string;
  message?: string;
}

export default class ErrorHandler {
  static lastNotificationDisplayedAt: DateTime | null = null;

  static showError(msg: string, error: IError | null, forceWarnUserOnError = false) {
    if (error instanceof CanceledError) return;

    GlobalEventBus.$emit(GlobalEvents.error, msg);
    if (!forceWarnUserOnError || error == null) {
      if (error == null || error.status === 401 || error.status === 403 || error.status === 404) {
        return;
      }
      if (error.status === HttpStatusCodeNoResponse) {
        GlobalEventBus.$emit(GlobalEvents.requestBlockedByClient);
        return;
      }
    }

    // Debounce errors, the user does not need to see a lot of notifications at once.
    if (
      ErrorHandler.lastNotificationDisplayedAt != null &&
      ErrorHandler.lastNotificationDisplayedAt > DateTime.local().minus({ milliseconds: 100 })
    ) {
      return;
    }
    ErrorHandler.lastNotificationDisplayedAt = DateTime.local();

    let formattedErrorMessage = msg;
    if (error?.response?.data?.error) {
      const errorMessage = error.response.data.error;
      formattedErrorMessage = `${msg}<br>Error: ${errorMessage}`;
      error.message += ` - ${errorMessage}`;
    }

    if (error !== undefined) {
      UserNotifications.error({
        title: null,
        message: error.userFriendlyMessage != null ? error.userFriendlyMessage : formattedErrorMessage,
        details: {
          label: i18n.global.t('messages.viewErrorReport'),
          modalTitle: capitalize(i18n.global.t('names.errorReport', { n: 1 })),
          content: JSON.stringify(error, null, 2),
        },
      });
    } else if (error.status === 500) {
      UserNotifications.error({
        title: null,
        message: formattedErrorMessage,
        details: {
          label: i18n.global.t('messages.viewErrorReport'),
          modalTitle: capitalize(i18n.global.t('names.errorReport', { n: 1 })),
          content: i18n.global.t('messages.internalServerError'),
        },
      });
    } else {
      UserNotifications.error({ title: null, message: formattedErrorMessage });
    }

    if (forceWarnUserOnError) {
      throw error;
    }
  }

  static handleExceptionInUIComponent(e) {
    if (!(e instanceof CanceledError)) {
      GlobalEventBus.$emit(GlobalEvents.somethingWentWrong);
      throw e;
    }
  }

  static isCancelledError(error: IError | null): boolean {
    return error instanceof CanceledError;
  }
}
